import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface UploadProgress {
  progress: number;
  status: 'uploading' | 'processing' | 'complete' | 'error';
}

export interface ImageUploadResult {
  success: boolean;
  imageUrl?: string;
  imagePath?: string;
  error?: string;
}

export class UserImageUploadService {
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

  /**
   * Validate image file before upload
   */
  static validateFile(file: File): string | null {
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, WebP, or HEIC)';
    }
    
    if (file.size > this.MAX_FILE_SIZE) {
      return 'File size must be less than 5MB';
    }
    
    return null;
  }

  /**
   * Upload image file to Supabase storage
   */
  static async uploadImage(
    file: File, 
    recipeId: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ImageUploadResult> {
    try {
      // Validate file
      const validationError = this.validateFile(file);
      if (validationError) {
        return { success: false, error: validationError };
      }

      onProgress?.({ progress: 10, status: 'uploading' });

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { success: false, error: 'Authentication required' };
      }

      onProgress?.({ progress: 20, status: 'uploading' });

      // Generate file path: {user_id}/{recipe_id}/{timestamp}_{filename}
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `${timestamp}_${recipeId}.${fileExtension}`;
      const filePath = `${user.id}/${recipeId}/${fileName}`;

      onProgress?.({ progress: 40, status: 'uploading' });

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('forkast_user')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        return { success: false, error: 'Failed to upload image' };
      }

      onProgress?.({ progress: 80, status: 'processing' });

      // Get signed URL with 1 year expiration (same as scraped images)
      const { data: urlData, error: urlError } = await supabase.storage
        .from('forkast_user')
        .createSignedUrl(data.path, 31536000); // 1 year = 365 * 24 * 60 * 60

      if (urlError) {
        console.error('Signed URL generation error:', urlError);
        return { success: false, error: 'Failed to generate image URL' };
      }

      onProgress?.({ progress: 100, status: 'complete' });

      return { success: true, imageUrl: urlData.signedUrl, imagePath: data.path };

    } catch (error) {
      console.error('Image upload error:', error);
      return { success: false, error: 'Failed to upload image' };
    }
  }

  /**
   * Delete image from storage
   */
  static async deleteImage(imageUrl: string): Promise<boolean> {
    try {
      // Extract path from URL
      const path = this.extractPathFromUrl(imageUrl);
      if (!path) return false;

      const { error } = await supabase.storage
        .from('forkast_user')
        .remove([path]);

      if (error) {
        console.error('Delete error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete image error:', error);
      return false;
    }
  }

  /**
   * Extract file path from Supabase storage URL (including signed URLs)
   */
  private static extractPathFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/');
      
      // Handle signed URLs - look for storage path patterns
      const storageIndex = pathSegments.findIndex(segment => segment === 'storage');
      const objectIndex = pathSegments.findIndex(segment => segment === 'object');
      
      if (storageIndex !== -1 && objectIndex !== -1 && objectIndex > storageIndex) {
        // Extract path after /storage/v1/object/sign/forkast_user/ or /storage/v1/object/forkast_user/
        const bucketIndex = pathSegments.findIndex(segment => segment === 'forkast_user');
        if (bucketIndex !== -1 && bucketIndex < pathSegments.length - 1) {
          return pathSegments.slice(bucketIndex + 1).join('/');
        }
      }
      
      // Fallback for direct bucket URLs
      const bucketIndex = pathSegments.findIndex(segment => segment === 'forkast_user');
      if (bucketIndex !== -1 && bucketIndex < pathSegments.length - 1) {
        return pathSegments.slice(bucketIndex + 1).join('/');
      }
      
      return null;
    } catch {
      return null;
    }
  }
}
